import { Chart, ChartDataPoint, ChartConfiguration, Flex, Span, Button } from "@goodfill/ui-components";
import { Decimal } from "decimal.js";
import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { findAllPossibleSortedRatingsValuesAmongBonds } from "./findAllPossibleSortedRatingsValuesAmongBonds";
import { PricedBond } from "../../PricedBond";
import { createIndexOfBondsPerIssuer } from "./createIndexOfBondsPerIssuer";
import { calculateIssuersColor } from "./calculateIssuersColor";
import { route } from "../../../../routes";
import { CorporateBondId, asBondPriceNominal } from "@goodfill/domain-properties";
import {
  formatPrice,
  formatCouponPayment,
  formatYield,
  formatMaturityDate,
} from "@goodfill/domain-properties-components";
import { formatPercentage } from "@goodfill/format-number";
import "./BondChart.scss";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

export interface BondPrices {
  gSpread: number;
  yieldPercentage: number;
}

export interface BondsChartProps {
  axisLabelGSpread?: string;
  axisLabelMaturity?: string;
  axisLabelMoodyRating?: string;
  axisLabelSpRating?: string;
  axisLabelPrice?: string;
  axisLabelYieldPercentage?: string;
  pricedBonds: PricedBond[];
  selectedXAxisOption: XAxisOption;
  selectedYAxisOption: YAxisOption;
  setSelectedXAxisOption: (_: XAxisOption) => void;
  setSelectedYAxisOption: (_: YAxisOption) => void;
  chartWrapperStyle: React.CSSProperties;
  isRow?: boolean;
}

export interface ChartTooltipInfo {
  bondId: CorporateBondId;
  coupon: string;
  issuerName: string;
  paymentRank: string;
  maturityDate?: string;
  price: string;
  yield: string;
}

// Stores a ChartDataPoint with a Bond id association
type BondDataPoint = ChartDataPoint & { id: CorporateBondId };

const xAxisOptions = ["maturity", "spRating", "moodyRating"] as const;
export type XAxisOption = typeof xAxisOptions[number];

const yAxisOptions = ["yieldPercentage", "gSpread", "price"] as const;
export type YAxisOption = typeof yAxisOptions[number];

const tooltipLabelIndentation = "  ";

export const BondsChart: React.FC<BondsChartProps> = ({
  pricedBonds,
  axisLabelMoodyRating = "Moody's",
  axisLabelSpRating = "S&P",
  axisLabelMaturity = "Maturity",
  axisLabelGSpread = "G-Spread",
  axisLabelYieldPercentage = "Yield",
  axisLabelPrice = "Price",
  selectedXAxisOption,
  setSelectedXAxisOption,
  selectedYAxisOption,
  setSelectedYAxisOption,
  chartWrapperStyle,
  isRow,
}) => {
  const [bondDataPoints, setBondDataPoints] = useState<BondDataPoint[]>([]);

  const xAxisOptionLabelRecord: Record<XAxisOption, string> = {
    moodyRating: axisLabelMoodyRating,
    spRating: axisLabelSpRating,
    maturity: axisLabelMaturity,
  };
  const yAxisOptionLabelRecord: Record<YAxisOption, string> = {
    yieldPercentage: axisLabelYieldPercentage,
    gSpread: axisLabelGSpread,
    price: axisLabelPrice,
  };

  const xAxisOptionSelectors = xAxisOptions.map((xAxisOption) => ({
    key: xAxisOption,
    label: xAxisOptionLabelRecord[xAxisOption],
    selected: selectedXAxisOption === xAxisOption,
    onClick: () => setSelectedXAxisOption(xAxisOption),
  }));
  const yAxisOptionSelectors = yAxisOptions.map((yAxisOption) => ({
    key: yAxisOption,
    label: yAxisOptionLabelRecord[yAxisOption],
    selected: selectedYAxisOption === yAxisOption,
    onClick: () => setSelectedYAxisOption(yAxisOption),
  }));

  const handleOnClick = (point: ChartDataPoint) => {
    for (const { id, datasetIndex, index } of bondDataPoints) {
      if (point.datasetIndex === datasetIndex && point.index === index) {
        const url = `${document.location.origin}${route.orderBuy(id)}`;
        window.open(url, "_blank");
        break;
      }
    }
  };

  const chartConfiguration: ChartConfiguration = useMemo(() => {
    const defaultPointConfiguration = {
      pointRadius: 5,
      pointHoverRadius: 8,
    };

    const selectedSpRatings = findAllPossibleSortedRatingsValuesAmongBonds(pricedBonds, "sp");
    const selectedMoodyRatings = findAllPossibleSortedRatingsValuesAmongBonds(pricedBonds, "moodys");

    const nextBondDataPoints: BondDataPoint[] = [];

    const yTicksCallback = (value: number) => {
      switch (selectedYAxisOption) {
        case "yieldPercentage":
          return formatPercentage({
            value: Number(value) / 100,
            numDecimals: 2,
            zeroAligned: true, // in chart, we want to have all values aligned
          });
        case "gSpread":
          return `${value} bps`;
        case "price":
          return formatPrice({
            value: asBondPriceNominal(new Decimal(value)),
          });
        default:
          return "";
      }
    };

    switch (true) {
      case selectedXAxisOption === "maturity": {
        const bondsByIssuer = createIndexOfBondsPerIssuer(pricedBonds);
        const datasets = Array.from(bondsByIssuer.entries()).map(([issuerId, pricedBonds], datasetIndex) => {
          const sortedBonds = pricedBonds.sort(
            // Sort by maturity.date first, in order to set nextBondDataPoints properly.
            (a, b) => (a.maturityDate < b.maturityDate ? -1 : 1),
          );

          // Need to serialize data into dataset label to show additional info in tooltip
          const tooltipInfo: ChartTooltipInfo[] = sortedBonds.map((pricedBond: PricedBond): ChartTooltipInfo => {
            return {
              bondId: pricedBond.bondIdCusip,
              coupon: formatCouponPayment({
                value: pricedBond.couponPayment,
              }),
              issuerName: pricedBond.issuerName,
              paymentRank: pricedBond.paymentRank,
              price: formatPrice({
                value: pricedBond.askPrice,
              }),
              yield: formatYield({
                value: pricedBond.askYield,
              }),
            };
          });

          return {
            backgroundColor: calculateIssuersColor(issuerId),
            borderColor: calculateIssuersColor(issuerId),
            data: sortedBonds.map((pricedBond, index) => {
              const maturityTimestamp = pricedBond.maturityDate.toMillis();
              if (isNaN(maturityTimestamp)) {
                return null;
              }

              nextBondDataPoints.push({
                id: pricedBond.bondIdCusip,
                datasetIndex,
                index,
              });

              switch (selectedYAxisOption) {
                case "yieldPercentage":
                  return {
                    x: maturityTimestamp,
                    y: pricedBond.askYield.mul(100).toNumber(),
                  };
                case "gSpread":
                  return {
                    x: maturityTimestamp,
                    y: pricedBond.askGSpread.toNumber(),
                  };
                case "price":
                  return {
                    x: maturityTimestamp,
                    y: pricedBond.askPrice.toNumber(),
                  };
                default:
                  return null;
              }
            }),
            label: JSON.stringify(tooltipInfo),
            ...defaultPointConfiguration,
          };
        });

        setBondDataPoints(nextBondDataPoints);

        const options = {
          scales: {
            x: {
              type: "time",
              time: {
                displayFormats: {
                  millisecond: "DD",
                  second: "DD",
                  minute: "DD",
                  hour: "DD",
                  day: "DD",
                  week: "DD",
                  month: "DD",
                  quarter: "DD",
                  year: "DD",
                },
              },
            },
            y: {
              ticks: {
                callback: yTicksCallback,
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                afterLabel: (context) => {
                  try {
                    const dataIndex = context.dataIndex;
                    const serializedData = context.dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const infoList: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const info = infoList[dataIndex];
                    return `${tooltipLabelIndentation}yield ${info.yield}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                beforeLabel: (context) => {
                  try {
                    const dataIndex = context.dataIndex;
                    const serializedData = context.dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const infoList: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const info = infoList[dataIndex];
                    return `${tooltipLabelIndentation}coupon ${info.coupon}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                label: (context) => {
                  try {
                    const dataIndex = context.dataIndex;
                    const serializedData = context.dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const infoList: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const info = infoList[dataIndex];
                    return `${tooltipLabelIndentation}price: ${info.price}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                beforeTitle: (context) => {
                  try {
                    const dataIndex = context[0].dataIndex;
                    const serializedData = context[0].dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const info: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const data = info[dataIndex];
                    if (typeof data !== "object" || data === null) {
                      return "";
                    }
                    return data.issuerName;
                  } catch (_ignore) {
                    return context[0].label;
                  }
                },
                title: (context) => {
                  try {
                    const dataIndex = context[0].dataIndex;
                    const serializedData = context[0].dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const info: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const data = info[dataIndex];
                    if (typeof data !== "object" || data === null) {
                      return "";
                    }
                    return data.paymentRank;
                  } catch (_ignore) {
                    return "error";
                  }
                },
                afterTitle: (context) => {
                  try {
                    const dataIndex = context[0].dataIndex;
                    const serializedData = context[0].dataset.label ?? null;
                    if (!serializedData) {
                      return "";
                    }
                    const infoList: ChartTooltipInfo[] = JSON.parse(serializedData);
                    const info = infoList[dataIndex];
                    return info.bondId;
                  } catch (_ignore) {
                    return "";
                  }
                },
                footer: (context) => {
                  try {
                    return `Maturity date: ${formatMaturityDate({
                      value: DateTime.fromMillis(context[0].parsed.x),
                      timezone: TIMEZONE_NEY_YORK,
                    })}`;
                  } catch (_ignore) {
                    return context[0].label;
                  }
                },
              },
              padding: 12,
            },
          },
        } as ChartConfiguration["options"];

        return {
          type: "line",
          data: {
            datasets,
          },
          options,
        };
      }

      case ["moodyRating", "spRating"].includes(selectedXAxisOption): {
        // Wrap categories with two fake values, to display them better.
        const selectedRatings = [""];
        if (selectedXAxisOption === "moodyRating") {
          selectedRatings.push(...selectedMoodyRatings);
        }
        if (selectedXAxisOption === "spRating") {
          selectedRatings.push(...selectedSpRatings);
        }
        selectedRatings.push(" ");

        const datasets = pricedBonds.map((pricedBond, datasetIndex) => {
          const ratingValue =
            selectedXAxisOption === "moodyRating" ? pricedBond.ratings.moodys : pricedBond.ratings.sp;

          const index = selectedRatings.findIndex((rating) => rating === ratingValue);

          nextBondDataPoints.push({
            id: pricedBond.bondIdCusip,
            datasetIndex,
            index,
          });

          // Need to serialize data into dataset label to show additional info in tooltip
          const tooltipInfo: ChartTooltipInfo = {
            bondId: pricedBond.bondIdCusip,
            coupon: formatCouponPayment({
              value: pricedBond.couponPayment,
            }),
            issuerName: pricedBond.issuerName,
            paymentRank: pricedBond.paymentRank,
            maturityDate: pricedBond.maturityDate.toISO(),
            price: formatPrice({
              value: pricedBond.askPrice,
            }),
            yield: formatYield({
              value: pricedBond.askYield,
            }),
          };

          return {
            backgroundColor: calculateIssuersColor(pricedBond.issuerId),
            data: selectedRatings.map((rating) => {
              if (rating !== ratingValue) {
                return null;
              }

              switch (selectedYAxisOption) {
                case "yieldPercentage":
                  return pricedBond.askYield.mul(100).toNumber();
                case "gSpread":
                  return pricedBond.askGSpread.toNumber();
                case "price":
                  return pricedBond.askPrice.toNumber();
                default:
                  return null;
              }
            }),
            label: JSON.stringify(tooltipInfo),
            ...defaultPointConfiguration,
          };
        });

        setBondDataPoints(nextBondDataPoints);

        const options = {
          plugins: {
            tooltip: {
              callbacks: {
                afterLabel: (context) => {
                  try {
                    const serializedInfo = context.dataset.label ?? null;
                    if (!serializedInfo) {
                      return context.label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return `${tooltipLabelIndentation}yield ${info.yield}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                beforeLabel: (context) => {
                  try {
                    const serializedInfo = context.dataset.label ?? null;
                    if (!serializedInfo) {
                      return context.label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return `${tooltipLabelIndentation}coupon ${info.coupon}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                label: (context) => {
                  try {
                    const serializedInfo = context.dataset.label ?? null;
                    if (!serializedInfo) {
                      return context.label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return `${tooltipLabelIndentation}price ${info.price}`;
                  } catch (_ignore) {
                    return "";
                  }
                },
                beforeTitle: (context) => {
                  try {
                    const serializedInfo = context[0].dataset.label ?? null;
                    if (!serializedInfo) {
                      return context[0].label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return info.issuerName;
                  } catch (_ignore) {
                    return context[0].label;
                  }
                },
                title: (context) => {
                  try {
                    const serializedInfo = context[0].dataset.label ?? null;
                    if (!serializedInfo) {
                      return context[0].label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return info.paymentRank;
                  } catch (_ignore) {
                    return context[0].label;
                  }
                },
                afterTitle: (context) => {
                  try {
                    const serializedInfo = context[0].dataset.label ?? null;
                    if (!serializedInfo) {
                      return context[0].label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    return info.bondId;
                  } catch (_ignore) {
                    return "";
                  }
                },
                footer: (context) => {
                  try {
                    const serializedInfo = context[0].dataset.label ?? null;
                    if (!serializedInfo) {
                      return context[0].label;
                    }
                    const info: ChartTooltipInfo = JSON.parse(serializedInfo);
                    const maturityDate = info.maturityDate;
                    return `Maturity date: ${
                      maturityDate !== undefined
                        ? formatMaturityDate({
                            value: DateTime.fromISO(maturityDate),
                            timezone: TIMEZONE_NEY_YORK,
                          })
                        : "N/A"
                    }`;
                  } catch (_ignore) {
                    return context[0].label;
                  }
                },
              },
              padding: 12,
            },
          },
          scales: {
            x: {
              type: "category",
              labels: selectedRatings,
            },
            y: {
              ticks: {
                callback: yTicksCallback,
              },
            },
          },
        } as ChartConfiguration["options"];

        return {
          type: "line",
          data: {
            datasets,
          },
          options,
        };
      }

      default: {
        setBondDataPoints(nextBondDataPoints);

        return {
          type: "line",
          data: {
            labels: [],
            datasets: [],
          },
        };
      }
    }
  }, [pricedBonds, selectedXAxisOption, selectedYAxisOption]);

  const labelOfX: Record<XAxisOption, string> = {
    maturity: axisLabelMaturity,
    spRating: axisLabelSpRating,
    moodyRating: axisLabelMoodyRating,
  };

  const labelOfY: Record<YAxisOption, string> = {
    yieldPercentage: axisLabelYieldPercentage,
    gSpread: axisLabelGSpread,
    price: axisLabelPrice,
  };

  const title = `${labelOfX[selectedXAxisOption]} and ${labelOfY[selectedYAxisOption]}`;

  const [screenWidth, setScreenWidth] = useState(0 as number);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    //cleanup listener
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  const chartWrapperStyleSmallScreens = {
    height: "400px",
    width: "100%",
  };

  return (
    <Flex direction="col" grow={1}>
      <Flex
        direction={`${!isRow ? "row" : "col"}`}
        justify="between"
        items="start"
        verticalMargin={2}
        gap={2}
        className={`${!isRow ? "BondChart_chartTitleOn1180" : "BondChart_chartTitleOn1700"} BondChart_chartsTitle`}
      >
        <Span
          size="xl"
          className={`font-semibold ${isRow && "flex justify-center w-full BondChart_paddingRightOn1700"}`}
        >
          {title}
        </Span>
        <Flex
          gap={2}
          className={`BondChart_pl4After1180 BondChart_breakOn400 ${
            isRow && "BondChart_compareInRow justify-between w-full"
          }`}
        >
          <Span size="sm" className={"pt-1.5 sm:mr-5 mr-1 mt-1"}>
            Y Axis Selection
          </Span>
          <div className="flex BondChart_smMargin gap-x-2">
            {yAxisOptionSelectors.map(({ key, label, onClick, selected }) => (
              <Button
                fill
                key={key}
                size="small"
                onClick={onClick}
                primary={selected}
                className="w-auto px-[18.5px] py-2 mt-1 text-[11px] min-w-[100px]"
              >
                {label}
              </Button>
            ))}
          </div>
        </Flex>
      </Flex>

      <div style={screenWidth > 930 ? chartWrapperStyle : chartWrapperStyleSmallScreens}>
        <Chart configuration={chartConfiguration} onClick={handleOnClick} />
      </div>

      <Flex
        direction={`${!isRow ? "row" : "col"}`}
        justify="center"
        verticalMargin={2}
        gap={2}
        items="center"
        className="BondChart_breakOn400"
      >
        <Span size="sm" className={`pt-1.5 ${!isRow && "sm:mr-16 mr-1"} `}>
          X Axis Selection
        </Span>
        <Flex gap={2}>
          {xAxisOptionSelectors.map(({ key, label, onClick, selected }) => (
            <Button
              fill
              key={key}
              size="small"
              onClick={onClick}
              primary={selected}
              className="w-auto px-[18.5px] py-2 mt-1 text-xs-imp"
            >
              {label}
            </Button>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};
